import { ArrowCaret, ArrowSwap, PersonGroup } from '@fs/zion-icon/dist/cjs/icons'
import { css } from '@emotion/core'
import { Button, LabelTooltip, ResponsiveTableRow, Row, Separator } from '@fs/zion-ui'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { usePageView } from '@fs/zion-analytics'
import { useTranslation } from 'react-i18next'
import { Link } from '@fs/zion-router'
import moment from 'moment'
import { DmcResponsiveTable } from '../../common/DmcResponsiveTable'
import { usePageTitle } from '../../app/PageTitleContext'
import useNotifications from '../../../hooks/useNotifications'
import ObjectView from '../../common/ObjectView'
import {
  getMergeHistory,
  getPersonReservationHistory,
  getReservationHistoryQuery,
} from '../../../services/personService'
import { getReservationPersonIds } from '../../../services/reservationsService'
import { utcToLocaleString } from '../../../util/DateUtil'
import DmcCopyToClipboardTooltip from '../../../util/DmcCopyToClipboardTooltip'
import { downloadFile } from '../../../util/FileDownloadUtil'
import { getEnumAbbreviation, getIdElement } from '../../../util/StringUtil'
import { CisLink, OsidLink, PersonLink, RanLink, UserLink } from '../../app/Links'
import { PersonContext } from '../PersonContext'
import { getUser } from '../../../services/userService'
import FilterOrdinances from '../ordinances/FilterOrdinances'

const sortableColumnHeaderBlock = css`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  font-weight: bold;
  padding: 4px;
  background-color: rgb(0, 0, 0, 0);
  border: none;
  border-radius: 6px;
  font-size: 16px;
  transition:
    font-size 200ms,
    background-color 200ms;
  :hover {
    background-color: rgb(90, 90, 90, 0.1);
  }
  :active {
    background-color: rgb(125, 125, 125, 0.2);
    font-size: 0.9rem;
  }
`
const sortingOnColumnHeaderBlock = css`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  font-weight: bold;
  padding: 4px;
  background-color: rgb(90, 90, 90, 0.1);
  border: none;
  border-radius: 6px;
  font-size: 16px;
  transition:
    font-size 200ms,
    background-color 200ms;
  :hover {
    background-color: rgb(90, 90, 90, 0.2);
  }
  :active {
    background-color: rgb(125, 125, 125, 0.3);
    font-size: 0.9rem;
  }
`
const sortableColumnHeaderInline = css`
  display: inline-flex;
  align-items: center;
  font-weight: bold;
  padding: 4px;
  background-color: rgb(0, 0, 0, 0);
  border: none;
  border-radius: 6px;
  font-size: 16px;
  transition:
    font-size 200ms,
    background-color 200ms;
  :hover {
    background-color: rgb(90, 90, 90, 0.1);
  }
  :active {
    background-color: rgb(125, 125, 125, 0.2);
    font-size: 0.9rem;
  }
`
const sortingOnColumnHeaderInline = css`
  display: inline-flex;
  align-items: center;
  font-weight: bold;
  padding: 4px;
  background-color: rgb(90, 90, 90, 0.1);
  border: none;
  border-radius: 6px;
  font-size: 16px;
  transition:
    font-size 200ms,
    background-color 200ms;
  :hover {
    background-color: rgb(90, 90, 90, 0.2);
  }
  :active {
    background-color: rgb(125, 125, 125, 0.3);
    font-size: 0.9rem;
  }
`

const SortableColumnHeaderComponent = ({
  fieldType,
  label,
  block,
  sortField,
  sortByFieldType,
  getFieldSortingIcon,
  blockCss,
  sortingOnBlockCss,
  inlineCss,
  sortingOnInlineCss,
}) => {
  return (
    <>
      {block ? (
        <button
          css={sortField === fieldType ? sortingOnBlockCss : blockCss}
          type="button"
          onClick={() => sortByFieldType(fieldType)}
        >
          <span style={{ flex: '1' }}>{label}</span>
          <span>{getFieldSortingIcon(fieldType)}</span>
        </button>
      ) : (
        <button
          css={sortField === fieldType ? sortingOnInlineCss : inlineCss}
          type="button"
          onClick={() => sortByFieldType(fieldType)}
        >
          <span style={{ flex: '1' }}>{label}</span>
          <span>{getFieldSortingIcon(fieldType)}</span>
        </button>
      )}
    </>
  )
}

const RetryFailedButton = ({ reservationHistoryStatuses, fetchReservationsHistory, disabled, t }) => {
  const handleClick = () => {
    Object.entries(reservationHistoryStatuses).forEach(([personId, status]) => {
      if (status === t('person.reservations.history.error', 'Error')) {
        fetchReservationsHistory(personId, () => getPersonReservationHistory(personId))
      }
    })
  }

  if (!disabled) {
    return (
      <Button emphasis="medium" onClick={handleClick}>
        {t('person.reservations.history.retry', 'Retry Failed Histories')}
      </Button>
    )
  }

  return null
}

export default function PersonReservationsHistory() {
  usePageView({ page_detail: 'DMConsole:PersonReservationsHistory' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.person.reservationsHistory'))
  const { error } = useNotifications()
  const { currentPerson } = useContext(PersonContext)
  const currentPersonId = currentPerson?.id
  const [reservationHistoryData, setReservationHistoryData] = useState(null)
  const [ranIdToPersonIdMap, setRanIdToPersonIdMap] = useState()
  const [userIdToContactNameMap, setUserIdToContactNameMap] = useState()
  const [sortField, setSortField] = useState('date')
  const [sortType, setSortType] = useState('descending')
  const [personReservationHistories, setPersonReservationHistories] = useState()
  const [ordinanceType, setOrdinanceType] = useState('all')
  const sortedReservationsHistory = sortReservationsHistory()
  const filteredReservationsHistory =
    ordinanceType === 'all'
      ? sortedReservationsHistory
      : sortedReservationsHistory?.filter((e) => e.reservation.ordinanceType === ordinanceType)
  const [reservationHistoryStatuses, setReservationHistoryStatuses] = useState({})
  const [loaded, setLoaded] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])

  const sortByFieldType = (field) => {
    if (field !== sortField || sortType === undefined) {
      setSortType('ascending')
      setSortField(field)
    } else if (sortType === 'ascending') {
      setSortType('descending')
    } else if (sortType === 'descending') {
      setSortType(undefined)
      setSortField(undefined)
    }
  }

  const hasErrorStatus = useCallback(
    (status) => {
      return status === t('person.reservations.history.error', 'Error')
    },
    [t]
  )

  const getFieldSortingIcon = (field) => {
    if (field !== sortField || sortType === undefined) {
      return <ArrowSwap size="xs" direction="up" />
    }
    if (sortType === 'ascending') {
      return <ArrowCaret size="xs" direction="up" />
    }
    if (sortType === 'descending') {
      return <ArrowCaret size="xs" direction="down" />
    }
    return null
  }

  const renderSortableColumnHeader = (fieldType, label, block) => (
    <SortableColumnHeaderComponent
      fieldType={fieldType}
      label={label}
      block={block}
      sortField={sortField}
      sortByFieldType={sortByFieldType}
      getFieldSortingIcon={getFieldSortingIcon}
      blockCss={sortableColumnHeaderBlock}
      sortingOnBlockCss={sortingOnColumnHeaderBlock}
      inlineCss={sortableColumnHeaderInline}
      sortingOnInlineCss={sortingOnColumnHeaderInline}
    />
  )

  const smallMediumTableRows = () => {
    return {
      labels: [
        <>
          {renderSortableColumnHeader('pid', t('person.reservations.history.pid', 'PID'))}/
          {renderSortableColumnHeader('ordinanceType', t('person.reservations.history.ord', 'Ord'))}
        </>,
        <>
          {renderSortableColumnHeader('date', t('person.reservations.history.date', 'Date'))}
          {`/${t('person.reservations.history.updateType', 'Update Type')}/${t(
            'person.reservations.history.action',
            'Action'
          )}/`}
          {renderSortableColumnHeader('cisId', t('person.reservations.history.submitterId', 'Submitter ID'))}
          {`/${t('person.reservations.history.contributorId', 'Contributor ID')}`}
        </>,
        <>
          {`${t('person.reservations.history.ranId', 'RAN ID')}/${t(
            'person.reservations.history.resType',
            'Res Type'
          )}/${t('person.reservations.history.owsOSID', 'OWS OSID')}/`}
          {renderSortableColumnHeader('ownerId', t('person.reservations.history.ownerId', 'Owner ID'))}
        </>,
      ],
      columnWidths: [3, 5, 4],
      combinationRules: [
        ['column1', 'column2'],
        ['column3', 'column4', 'column5', 'column6', 'column7'],
        ['column8', 'column9', 'column10', 'column11'],
      ],
    }
  }

  const defaultXxlTableRows = () => ({
    labels: [
      renderSortableColumnHeader('pid', t('person.reservations.history.pid', 'PID'), true),
      renderSortableColumnHeader('ordinanceType', t('person.reservations.history.ord', 'Ord'), true),
      renderSortableColumnHeader('date', t('person.reservations.history.date', 'Date'), true),
      `${t('person.reservations.history.updateType', 'Update Type')}/${t(
        'person.reservations.history.action',
        'Action'
      )}`,
      <>
        {renderSortableColumnHeader('cisId', t('person.reservations.history.submitterId', 'Submitter ID'))}
        {`/${t('person.reservations.history.contributorId', 'Contributor ID')}`}
      </>,
      `${t('person.reservations.history.ranId', 'RAN ID')}/${t(
        'person.reservations.history.resType',
        'Res Type'
      )}/${t('person.reservations.history.owsOSID', 'OWS OSID')}`,
      renderSortableColumnHeader('ownerId', t('person.reservations.history.ownerId', 'Owner ID')),
    ],
    columnWidths: [1, 1, 1, 3, 2, 2, 2],
    combinationRules: [
      ['column1'],
      ['column2'],
      ['column3'],
      ['column4', 'column5'],
      ['column6', 'column7'],
      ['column8', 'column9', 'column10'],
      ['column11'],
    ],
  })

  const fetchReservationsHistory = useCallback(
    async (personId, getResHistoryFunc, attempt = 1) => {
      try {
        setReservationHistoryStatuses((prevState) => {
          return { ...prevState, [personId]: t('person.reservations.history.status.RUNNING', 'Running') }
        })
        const response = await getResHistoryFunc()
        if (response.data.status === 'COMPLETED') {
          setReservationHistoryStatuses((prevState) => {
            return {
              ...prevState,
              [personId]: t('person.reservations.history.completedResults', 'Completed: {numResults} results', {
                numResults: response.data.events.length,
              }),
            }
          })

          setPersonReservationHistories((prevState) => {
            return { ...prevState, [personId]: response.data.events }
          })
        } else if (response.data.status === 'RUNNING') {
          setTimeout(() => {
            fetchReservationsHistory(
              personId,
              () => getReservationHistoryQuery(response.data.queryExecutionId),
              attempt + 1
            )
          }, attempt * 1000)
        } else {
          setReservationHistoryStatuses((prevState) => {
            return {
              ...prevState,
              [personId]: t('person.reservations.history.status.{status}', '{status}', {
                status: response.data.status,
              }),
            }
          })
        }
      } catch (err) {
        setReservationHistoryStatuses((prevState) => {
          return {
            ...prevState,
            [personId]: t('person.reservations.history.error', 'Error'),
          }
        })
        setErrorMessages((prevState) => {
          return [...prevState, err.message]
        })
      }
    },
    [t]
  )

  useEffect(() => {
    if (loaded) {
      const errorFound = Object.entries(reservationHistoryStatuses).some(([, status]) => hasErrorStatus(status))
      setHasError(errorFound)
      const uniqueErrorMessages = [...new Set(errorMessages)]
      const msg = uniqueErrorMessages.length > 0 ? uniqueErrorMessages.join(', ') : ''
      if (errorFound) {
        error(
          `${t(
            'person.reservationsHistory.errorFetchingPersonReservationHistory',
            `Error retrieving person reservations history`
          )}: ${msg}`
        )
      }
    }
  }, [loaded, reservationHistoryStatuses, errorMessages, error, t, hasErrorStatus])

  useEffect(() => {
    const result = []
    personReservationHistories &&
      Object.entries(personReservationHistories).forEach((entry) => {
        const [key, value] = entry
        value.forEach((reservationEntry) => {
          reservationEntry.personId = key
        })
        result.push(...value)
      })
    personReservationHistories && setReservationHistoryData(result)
  }, [personReservationHistories])

  useEffect(() => {
    if (currentPersonId) {
      fetchReservationsHistory(currentPersonId, () => getPersonReservationHistory(currentPersonId))
    }
  }, [currentPersonId, fetchReservationsHistory])

  const buildInitialMergesMap = (mergeHistoryData) => {
    const initialMerges = {}
    mergeHistoryData.reverse().forEach((item) => {
      if (item.operation === 'Merged' && !initialMerges[item.duplicatePersonId]) {
        initialMerges[item.duplicatePersonId] = item.changeId.slice(0, -4)
      }
    })
    return initialMerges
  }

  const getInitialMerges = useCallback(async () => {
    const response = await getMergeHistory(currentPersonId)
    return buildInitialMergesMap(response.data)
  }, [currentPersonId])

  const hasRestoredOperation = useCallback((mergeData) => {
    return mergeData.some((item) => item.operation === 'Restored')
  }, [])

  const processMergeEntry = useCallback(
    async (duplicatePersonId, changeId) => {
      const mergeResponse = await getMergeHistory(duplicatePersonId)
      const mergeIndex = mergeResponse.data.findIndex((item) => item.changeId.slice(0, -4) === changeId)
      const relevantMergeData = mergeIndex === -1 ? mergeResponse.data : mergeResponse.data.slice(0, mergeIndex)

      if (!hasRestoredOperation(relevantMergeData)) {
        return duplicatePersonId
      }
      return null
    },
    [hasRestoredOperation]
  )

  const getFinalMergeIds = useCallback(
    async (initialMerges) => {
      if (!initialMerges) {
        return []
      }

      const entries = Object.entries(initialMerges)
      const results = await Promise.all(
        entries.map(([duplicatePersonId, changeId]) => processMergeEntry(duplicatePersonId, changeId))
      )

      return results.filter((id) => id !== null)
    },
    [processMergeEntry]
  )

  const fetchRelatedPersonReservationHistories = useCallback(async () => {
    const initialMerges = await getInitialMerges()
    const finalMergedIds = await getFinalMergeIds(initialMerges)
    const uniqueMergedIds = [...new Set(finalMergedIds)]

    const reservationHistoryPromises = uniqueMergedIds.map((mergedId) =>
      fetchReservationsHistory(mergedId, () => getPersonReservationHistory(mergedId))
    )

    await Promise.allSettled(reservationHistoryPromises)
    setLoaded(true)
  }, [getInitialMerges, getFinalMergeIds, fetchReservationsHistory])

  useEffect(() => {
    fetchRelatedPersonReservationHistories(currentPersonId)
  }, [currentPersonId, fetchRelatedPersonReservationHistories])

  useEffect(() => {
    const fetchReservationPersonIds = async () => {
      if (reservationHistoryData) {
        const uniqueRanIds = [
          ...new Set(reservationHistoryData.map((reservationEvent) => reservationEvent.reservation.reservationId)),
        ]
        const fetchReservationPersonIdsPromises = uniqueRanIds.map((ranId) => {
          return getReservationPersonIds(ranId)
        })
        const fetchReservationPersonIdsResults = await Promise.allSettled(fetchReservationPersonIdsPromises)
        const ranIdToPersonMapObject = {}
        fetchReservationPersonIdsResults
          .filter((result) => result.status === 'fulfilled')
          .forEach((result) => {
            const resultUrlSplit = result.value.config.url.split('/')
            const ranId = resultUrlSplit[resultUrlSplit.length - 2]
            ranIdToPersonMapObject[ranId] = result.value.data[0]
          })
        setRanIdToPersonIdMap(ranIdToPersonMapObject)
      }
    }
    if (reservationHistoryData && !ranIdToPersonIdMap) {
      fetchReservationPersonIds()
    }
  }, [ranIdToPersonIdMap, reservationHistoryData])

  useEffect(() => {
    const fetchContactNames = async () => {
      if (reservationHistoryData) {
        const uniqueCisIds = [...new Set(reservationHistoryData.map((reservationEvent) => reservationEvent.cisId))]
        const uniqueProxyCisIds = [
          ...new Set(reservationHistoryData.map((reservationEvent) => reservationEvent.proxyCisId)),
        ]
        const uniqueOwnerIds = [
          ...new Set(reservationHistoryData.map((reservationEvent) => reservationEvent.reservation.ownerId)),
        ]
        const fetchContactNamesForCisIdsPromises = uniqueCisIds.map((cisId) => {
          return getUser(cisId, 'cisId')
        })
        const fetchContactNamesForProxyCisIdsPromises = uniqueProxyCisIds.map((proxyCisId) => {
          return getUser(proxyCisId, 'cisId')
        })
        const fetchContactNamesForOwnerIdsPromises = uniqueOwnerIds.map((ownerId) => {
          return getUser(ownerId, 'contributorId')
        })
        const fetchReservationPersonIdsResults = await Promise.allSettled([
          ...fetchContactNamesForCisIdsPromises,
          ...fetchContactNamesForProxyCisIdsPromises,
          ...fetchContactNamesForOwnerIdsPromises,
        ])
        const userIdToContactNameMapObject = {}
        fetchReservationPersonIdsResults
          .filter((result) => result.status === 'fulfilled')
          .forEach((result) => {
            const resultUrlSplit = result.value.config.url.split('/')
            const cisIdAndParamsSplit = resultUrlSplit[resultUrlSplit.length - 1].split('?')
            const userId = cisIdAndParamsSplit[0]
            if (userId.startsWith('cis.wkca')) {
              userIdToContactNameMapObject[userId] = result.value.data.identity.users[0]?.systemName
            } else if (userId.startsWith('cis.proc')) {
              userIdToContactNameMapObject[userId] = result.value.data.identity.users[0]?.displayName
            } else {
              userIdToContactNameMapObject[userId] = result.value.data.identity.users[0]?.contactName
            }
          })
        setUserIdToContactNameMap(userIdToContactNameMapObject)
      }
    }
    if (reservationHistoryData && !userIdToContactNameMap) {
      fetchContactNames()
    }
  }, [userIdToContactNameMap, reservationHistoryData])

  function sortReservationsHistory() {
    if (!reservationHistoryData) return reservationHistoryData

    const getItem = (item, path) => path.reduce((acc, key) => acc?.[key], item)
    const compare = (left, right, path, transform = (x) => x) => {
      const itemA = transform(getItem(left, path))
      const itemB = transform(getItem(right, path))
      return itemA?.localeCompare?.(itemB) || itemB - itemA
    }

    const sortByField = (path, transform) => {
      const sortedData = [...reservationHistoryData]
      return sortType === 'ascending'
        ? sortedData.sort((a, b) => compare(a, b, path, transform))
        : sortedData.sort((a, b) => compare(b, a, path, transform))
    }

    switch (sortField) {
      case 'date':
        return [...reservationHistoryData].sort((a, b) => {
          const timeA = moment.utc(a.timestamp).valueOf()
          const timeB = moment.utc(b.timestamp).valueOf()
          return sortType === 'ascending' ? timeA - timeB : timeB - timeA
        })
      case 'cisId':
        return sortByField(['cisId'], getIdElement)
      case 'pid':
        return sortByField(['personId'], getIdElement)
      case 'ownerId':
        return sortByField(['reservation', 'ownerId'])
      case 'ordinanceType':
        return sortByField(['reservation', 'ordinanceType'])
      default:
        return reservationHistoryData
    }
  }

  const getDataAsStringList = (rows) => {
    const stringList = []
    const columnsRow = `Ordinance Type, Event Timestamp, Update Type, Update Action, Submitter ID, Contributor ID, Request ID, Developer Key, RAN ID, Reservation Type, Owner ID, Owner CIS ID, OWS OSID, Schema Version, Authorization ID, Current Spouse 1 ID, Current Spouse 2 ID, Original Spouse 1 ID, Original Spouse 2 ID, Current Individual ID, Original Individual ID, Create Timestamp, Assign Timestamp, Printable Inventory Timestamp, Select Timestamp, Update Timestamp, Shared with Church, Selected, On List`
    stringList.push([columnsRow])

    if (rows) {
      rows.forEach((reservationEvent) => {
        const data = `${reservationEvent.reservation.ordinanceType},   ${utcToLocaleString(
          reservationEvent.timestamp,
          true
        )},    ${reservationEvent.updateType},    ${reservationEvent.updateAction},    ${reservationEvent.cisId},    ${
          reservationEvent.proxyCisId
        },    ${reservationEvent.requestId},    ${reservationEvent.developerKey},    ${
          reservationEvent.reservation.reservationId
        },    ${reservationEvent.reservation.reservationType},    ${reservationEvent.reservation.ownerId},    ${
          reservationEvent.reservation.ownerCisId
        },    ${reservationEvent.reservation.templeSystemId && reservationEvent.reservation.templeSystemId},    ${
          reservationEvent.reservation.schemaVersion
        },    ${reservationEvent.reservation.authorizationId},    ${reservationEvent.reservation.currentSpouse1Id},    ${
          reservationEvent.reservation.currentSpouse2Id
        },    ${reservationEvent.reservation.originalSpouse1Id},    ${
          reservationEvent.reservation.originalSpouse2Id
        },    ${reservationEvent.reservation.currentIndividualId},    ${
          reservationEvent.reservation.originalIndividualId
        },    ${utcToLocaleString(reservationEvent.reservation.createTimestamp, true)},    ${utcToLocaleString(
          reservationEvent.reservation.assignTimestamp,
          true
        )},    ${utcToLocaleString(
          reservationEvent.reservation.printableInventoryTimestamp,
          true
        )},    ${utcToLocaleString(reservationEvent.reservation.selectTimestamp, true)},    ${utcToLocaleString(
          reservationEvent.reservation.updateTimestamp,
          true
        )},    ${reservationEvent.reservation.sharedWithChurch},    ${reservationEvent.reservation.selected},    ${
          reservationEvent.reservation.onList
        }`
        stringList.push(data)
      })
    }

    return stringList
  }

  const downloadButtonOnClick = () => {
    const reservationHistoryDataAsStringList = getDataAsStringList(filteredReservationsHistory)
    const reservationHistoryDataAsOneString = reservationHistoryDataAsStringList.join('\n')
    downloadFile(reservationHistoryDataAsOneString, `${currentPersonId}-reservations-history.csv`)
  }

  return (
    <>
      {reservationHistoryStatuses && <ObjectView object={reservationHistoryStatuses} noCamelCase />}
      <Separator />
      {sortedReservationsHistory && sortedReservationsHistory.length > 0 && (
        <>
          <Row>
            <FilterOrdinances
              currentOrdinanceType={ordinanceType}
              setOrdinanceType={setOrdinanceType}
              isReservationList
            />
            <Button emphasis="medium" onClick={downloadButtonOnClick}>
              {t('action.download', 'Download')}
            </Button>
            <RetryFailedButton
              reservationHistoryStatuses={reservationHistoryStatuses}
              fetchReservationsHistory={fetchReservationsHistory}
              disabled={!hasError}
              t={t}
            />
          </Row>

          <Separator size="nano" />
          {filteredReservationsHistory.length === 0 &&
            t('person.reservations.history.noEventsFound', 'No events found')}
          {filteredReservationsHistory.length > 0 && (
            <DmcResponsiveTable
              id="Reservations History Table"
              gutters="nano"
              totalColumnCount={11}
              separator={<br />}
              atSizeColumns={{
                default: defaultXxlTableRows(),
                xxs: smallMediumTableRows(),
                xs: smallMediumTableRows(),
                sm: smallMediumTableRows(),
                md: smallMediumTableRows(),
                xl: {
                  labels: [
                    <>
                      {renderSortableColumnHeader('pid', t('person.reservations.history.pid', 'PID'))}/
                      {renderSortableColumnHeader('ordinanceType', t('person.reservations.history.ord', 'Ord'))}
                    </>,
                    <>
                      {renderSortableColumnHeader('date', t('person.reservations.history.date', 'Date'))}
                      {`/${t('person.reservations.history.updateType', 'Update Type')}/${t(
                        'person.reservations.history.action',
                        'Action'
                      )}`}
                    </>,
                    <>
                      {renderSortableColumnHeader(
                        'cisId',
                        t('person.reservations.history.submitterId', 'Submitter ID')
                      )}
                      {`/${t('person.reservations.history.contributorId', 'Contributor ID')}`}
                    </>,
                    `${t('person.reservations.history.ranId', 'RAN ID')}/${t(
                      'person.reservations.history.resType',
                      'Res Type'
                    )}/${t('person.reservations.history.owsOSID', 'OWS OSID')}`,
                    renderSortableColumnHeader('ownerId', t('person.reservations.history.ownerId', 'Owner ID')),
                  ],
                  columnWidths: [1, 4, 3, 3, 1],
                  combinationRules: [
                    ['column1', 'column2'],
                    ['column3', 'column4', 'column5'],
                    ['column6', 'column7'],
                    ['column8', 'column9', 'column10'],
                    ['column11'],
                  ],
                },
                xxl: defaultXxlTableRows(),
              }}
            >
              {filteredReservationsHistory.map((reservationEvent, index, array) => {
                return (
                  <ResponsiveTableRow
                    key={JSON.stringify(reservationEvent)}
                    noBottomDivider={array.length - 1 === index}
                    column1={
                      <span style={{ wordWrap: 'break-word' }}>
                        <PersonLink personId={reservationEvent.personId} />
                      </span>
                    }
                    column2={getEnumAbbreviation(reservationEvent.reservation.ordinanceType)}
                    column3={<span>{utcToLocaleString(reservationEvent.timestamp, true)}</span>}
                    column4={
                      reservationEvent.updateType && (
                        <span style={{ wordWrap: 'break-word' }}>
                          {t(`reservation.updateType.${reservationEvent.updateType}`, reservationEvent.updateType)}
                        </span>
                      )
                    }
                    column5={
                      reservationEvent.updateAction && (
                        <span style={{ wordWrap: 'break-word' }}>
                          {t(
                            `reservation.updateAction.${reservationEvent.updateAction}`,
                            reservationEvent.updateAction
                          )}
                        </span>
                      )
                    }
                    column6={
                      <>
                        {reservationEvent.cisId && (
                          <LabelTooltip
                            target={
                              reservationEvent.cisId?.startsWith('cis.user') ? (
                                <CisLink userId={getIdElement(reservationEvent.cisId)} />
                              ) : (
                                reservationEvent.cisId
                              )
                            }
                            label={userIdToContactNameMap?.[reservationEvent.cisId]}
                          />
                        )}
                      </>
                    }
                    column7={
                      <>
                        {reservationEvent.proxyCisId && (
                          <LabelTooltip
                            target={
                              reservationEvent.proxyCisId?.startsWith('cis.user') ? (
                                <CisLink userId={getIdElement(reservationEvent.proxyCisId)} />
                              ) : (
                                reservationEvent.proxyCisId
                              )
                            }
                            label={userIdToContactNameMap?.[reservationEvent.proxyCisId]}
                          />
                        )}
                      </>
                    }
                    column8={
                      <>
                        <DmcCopyToClipboardTooltip
                          value={reservationEvent.reservation.reservationId}
                          body={
                            ranIdToPersonIdMap?.[reservationEvent.reservation.reservationId] ? (
                              <RanLink
                                ranId={reservationEvent.reservation.reservationId}
                                pid={ranIdToPersonIdMap[reservationEvent.reservation.reservationId]}
                                newTab
                              />
                            ) : (
                              reservationEvent.reservation.reservationId
                            )
                          }
                        />
                        {reservationEvent.reservation.authorizationId && (
                          <>
                            &nbsp;&nbsp;&nbsp;
                            <Link
                              id="reservation-history-print"
                              to={`reservations-authorization/${reservationEvent.reservation.authorizationId}`}
                              target="_blank"
                            >
                              <PersonGroup />
                            </Link>
                          </>
                        )}
                      </>
                    }
                    column9={
                      <LabelTooltip
                        target={getEnumAbbreviation(reservationEvent.reservation.reservationType)}
                        label={t(
                          `reservation.reservationType.${reservationEvent.reservation.reservationType}`,
                          reservationEvent.reservation.reservationType
                        )}
                      />
                    }
                    column10={
                      reservationEvent.reservation.templeSystemId ? (
                        <OsidLink osid={reservationEvent.reservation.templeSystemId} />
                      ) : (
                        t('person.reservations.history.noTempleSystemId', `None`)
                      )
                    }
                    column11={
                      <>
                        {reservationEvent.reservation.ownerId && (
                          <LabelTooltip
                            target={<UserLink userId={reservationEvent.reservation.ownerId} newTab />}
                            label={userIdToContactNameMap?.[reservationEvent.reservation.ownerId]}
                          />
                        )}
                      </>
                    }
                  />
                )
              })}
            </DmcResponsiveTable>
          )}
        </>
      )}
      {sortedReservationsHistory?.length === 0 && t('person.reservations.history.noEventsFound', `No events found`)}
    </>
  )
}
